// REGULA SCĂRII — una singură, pentru desen și pentru import.
//
// CE SE CALCULEAZĂ ȘI CE SE TRAGE DE MÂNĂ. Până acum scara era o cutie cu linii
// la 28 cm: numărul de trepte ieșea din cât de adâncă o trăgeai, deci o scară
// scurtată „pierdea" trepte și urca tot un etaj. Pe planșă asta e o scară care
// nu se poate construi.
//
// Ordinea adevărată e cea din Revit (v. videoul lui Răzvan, 28.09.2026):
//
//   1. ÎNĂLȚIMEA DE URCAT e dată — e înălțimea nivelului, 2,86 în video.
//   2. NUMĂRUL DE CONTRATREPTE iese din ea: atâtea cât să nu treci de pasul
//      maxim admis. 2,86 / 0,1875 = 15,25 → 16. („Desired Number of Risers")
//   3. PASUL REAL se împarte înapoi: 2,86 / 16 = 17,875. („Actual Riser Height")
//   4. ADÂNCIMEA TREPTEI e ce rămâne: lungimea rampei împărțită la treptele ei.
//      („Actual Tread Depth" — 28,00 în video.)
//
// Deci numărul de trepte NU se schimbă când tragi de scară; se schimbă
// adâncimea lor. O scară trasă mai lungă are trepte mai comode, nu mai multe —
// exact ce arată Revit când muți capătul unei rampe.

/** PASUL MAXIM, în metri. 0,1875 e cifra cu care videoul scoate 16
 *  contratrepte dintr-un nivel de 2,86. Normele noastre cer 17,5 la locuințe,
 *  dar cifra asta e a TIPULUI de scară, nu a proiectului, iar tipul desenat în
 *  video e cel pe care-l reproducem. */
export const PAS_MAX = 0.1875;

/** Adâncimea de treaptă pe care o ținem când scara e destul de lungă ca s-o
 *  încapă — 28 cm, cifra din video și minimul obișnuit pe o scară de bloc. */
export const TREAPTA = 0.28;

/** FORMULA LUI BLONDEL — pasul de mers: `2 × h + l = 62…64 cm`, unde `h` e
 *  înălțimea contratreptei și `l` lățimea (adâncimea) treptei.
 *
 *  DE CE E O BANDĂ, NU O CIFRĂ: pasul omului pe orizontală e cam 63 cm, iar
 *  urcând, fiecare centimetru de ridicare îi mănâncă doi din pas. Două
 *  centimetri de joc în jurul lui nu se simt sub talpă, deci o pereche care cade
 *  în bandă e bună așa cum e — de-aia cifra scrisă de om se păstrează cât timp
 *  perechea rămâne înăuntru, și se îndreaptă abia când iese.
 *
 *  Cerut de arhitect (21.10.2026): «Calculul treptei și contratreptei se face
 *  folosind formula lui Blondel (a pasului de mers): 2 × h + l = 62 - 64 cm».
 */
export const BLONDEL_MIN = 0.62;
export const BLONDEL_MAX = 0.64;

/** Spre ce se îndreaptă o pereche ieșită din bandă: mijlocul ei. */
export const BLONDEL_TINTA = 0.63;

/** CONFORTUL MAXIM, tot de la arhitect: «înălțimea contratreptei este de regulă
 *  16–18 cm, iar adâncimea treptei de 28–30 cm».
 *
 *  E MAI STRÂMT DECÂT BLONDEL, și nu întâmplător: cele două capete se taie. Cu
 *  `h` între 16 și 18, formula cere `l` între 27 și 31; cerând și `l` între 28 și
 *  30, rămâne `h` între 16,5 și 17,5. Deci confortul alege dintre perechile
 *  bune pe cele mai bune, iar numărul de contratrepte se alege după el când
 *  nivelul lasă de ales. */
export const PAS_CONFORT: readonly [number, number] = [0.16, 0.18];
export const TREAPTA_CONFORT: readonly [number, number] = [0.28, 0.30];

/** Cât iese formula pentru o pereche dată — `2h + l`. */
export const blondel = (pas: number, treapta: number) => 2 * pas + treapta;

/** E perechea în bandă? */
export const blondelBun = (pas: number, treapta: number) => {
  const b = blondel(pas, treapta);
  return b >= BLONDEL_MIN - 1e-9 && b <= BLONDEL_MAX + 1e-9;
};

/** CÂTE CONTRATREPTE urcă `hNivel` cu o contratreaptă cât mai aproape de `pas`.
 *
 *  Contratreptele sunt un NUMĂR ÎNTREG, iar scara trebuie să ajungă fix la
 *  planșeul de deasupra: `n × h = hNivel`. Deci înălțimea cerută nu se poate
 *  păstra întocmai — se ia `n`-ul care o apropie cel mai mult, iar `h` iese din
 *  el. Asta răspunde și la «dacă scad înălțimea contratreptei, mai trebuie
 *  adăugată o treaptă?»: da, atâtea câte cere nivelul. */
export function contratrepteLaPas(hNivel: number, pas: number): number {
  if (!(pas > 0) || !(hNivel > 0)) return 2;
  return Math.max(2, Math.round(hNivel / pas));
}

/** CELE DOUĂ MĂRIMI ALE TREPTEI, potrivite una după alta prin Blondel.
 *
 *  Se scrie UNA, cealaltă iese — iar numărul de contratrepte se potrivește ca
 *  scara să urce tot nivelul. Cerut de arhitect (21.10.2026): «if we update
 *  lățimea treptei or înălțimea contratreptei it should automatically update
 *  the others».
 *
 *  CE SE PĂSTREAZĂ ȘI CE SE DEDUCE. Cifra scrisă de om e cea care comandă; a
 *  doua iese din formulă. Dar `h` nu poate fi păstrat întocmai, fiindcă el e
 *  `hNivel / n` cu `n` întreg (v. `contratrepteLaPas`) — deci scriind
 *  contratreapta, omul alege de fapt NUMĂRUL de contratrepte, iar înălțimea
 *  iese din el. Scriind treapta, ea se păstrează întocmai cât timp perechea
 *  rezultată cade în bandă; abia dacă iese, se îndreaptă spre țintă.
 *
 *  BANDA SE FOLOSEȘTE, nu se ocolește: o treaptă de 28 cm cu o contratreaptă de
 *  17,5 dă 63 — dar și 28,5 cu 17,5 dă 63,5, tot bună. Îndreptând-o de fiecare
 *  dată spre 63 exact, cifra scrisă de om i-ar fi sărit din mână la fiecare
 *  tastă. */
export function potrivesteBlondel(
  ce: { pas?: number; treapta?: number }, hNivel: number,
  acum?: { pas?: number; treapta?: number },
): { pas: number; treapta: number; contratrepte: number } {
  const laBanda = (v: number, jos: number, sus: number) =>
    Math.min(sus, Math.max(jos, v));
  if (ce.pas != null && ce.pas > 0) {
    // SCRIE CONTRATREAPTA: ea alege numărul de trepte, iar înălțimea iese.
    const n = contratrepteLaPas(hNivel, ce.pas);
    const pas = hNivel / n;
    const treapta = laBanda(BLONDEL_TINTA - 2 * pas,
                            BLONDEL_MIN - 2 * pas, BLONDEL_MAX - 2 * pas);
    return { pas, treapta, contratrepte: n };
  }
  if (ce.treapta != null && ce.treapta > 0) {
    // SCRIE TREAPTA: din ea iese contratreapta cerută, din ea numărul.
    const n = contratrepteLaPas(hNivel, (BLONDEL_TINTA - ce.treapta) / 2);
    const pas = hNivel / n;
    // treapta scrisă se ține cât timp perechea rămâne în bandă
    const treapta = blondelBun(pas, ce.treapta)
      ? ce.treapta
      : laBanda(BLONDEL_TINTA - 2 * pas,
                BLONDEL_MIN - 2 * pas, BLONDEL_MAX - 2 * pas);
    return { pas, treapta, contratrepte: n };
  }
  // N-A SCRIS NICIUNA: se îndreaptă perechea de ACUM. Ăsta e drumul prin care o
  // scară veche ajunge la normă fără ca nimeni să-i fi scris o cifră.
  const pas0 = acum?.pas;
  const t0 = acum?.treapta ?? TREAPTA;
  // CE E DEJA BUN NU SE ATINGE: o pereche din bandă rămâne exact cum e, cu
  // numărul ei de contratrepte. Altfel îndreptarea ar fi mișcat și scările
  // sănătoase, doar ca să le ducă de la 63,8 la 63.
  if (pas0 && blondelBun(pas0, t0)) {
    return { pas: pas0, treapta: t0,
             contratrepte: contratrepteLaPas(hNivel, pas0) };
  }
  // IAR CE E STRICAT PLEACĂ DE LA NUMĂRUL CEL MAI COMOD AL NIVELULUI, nu de la
  // contratreapta de acum. Păstrată, o contratreaptă absurdă trăgea după ea o
  // treaptă absurdă: pe catalog, o scară cu h = 35 cm (opt contratrepte pentru
  // 2,80 m) cerea prin formulă o treaptă de MINUS 7 cm, iar una cu h = 9,3 cm
  // cerea 44 cm. Formula leagă cele două măsuri, dar nu poate repara o
  // contratreaptă care nu e de scară — aia se repară numărând altfel.
  const n = contratrepteComode(hNivel);
  const pas = hNivel / n;
  const treapta = blondelBun(pas, t0)
    ? t0
    : laBanda(BLONDEL_TINTA - 2 * pas,
              BLONDEL_MIN - 2 * pas, BLONDEL_MAX - 2 * pas);
  return { pas, treapta, contratrepte: n };
}

/** NUMĂRUL DE CONTRATREPTE CEL MAI COMOD pentru un nivel: cel care aduce și
 *  contratreapta, și treapta în intervalele de confort. Dacă niciunul nu le
 *  împlinește pe amândouă, cel mai apropiat de ele.
 *
 *  Pe nivelurile obișnuite iese câte unul singur: 2,80 → 16 (17,50 / 28,00),
 *  2,86 → 17 (16,82 / 29,35), 3,00 → 18 (16,67 / 29,67). */
export function contratrepteComode(hNivel: number): number {
  let celMaiBun = contratrepte(hNivel);
  let celCost = Infinity;
  for (let n = 2; n <= 40; n++) {
    const pas = hNivel / n;
    if (pas > PAS_MAX) continue;
    const treapta = BLONDEL_TINTA - 2 * pas;
    const peste = (v: number, [jos, sus]: readonly [number, number]) =>
      Math.max(0, jos - v) + Math.max(0, v - sus);
    const cost = peste(pas, PAS_CONFORT) + peste(treapta, TREAPTA_CONFORT);
    if (cost < celCost - 1e-9) { celCost = cost; celMaiBun = n; }
  }
  return celMaiBun;
}

/** THE RUN LENGTHS A STOREY ALLOWS, and the nearest one to a typed figure.
 *
 *  The storey fixes the riser count (`contratrepteComode`: on 2.80 only 16
 *  risers give a riser inside R07), and with it the riser height. The band
 *  and the comfort range then leave the tread a few millimetres of play:
 *  on 2.80, 27–29 by Blondel and 28–30 by comfort, so 28–29. The run is the
 *  treads laid end to end, one fewer than risers on every flight (its last
 *  riser lands on the landing or the floor), so the run can only be
 *  `(n − k) × l` with `l` in that play.
 *
 *  Tested 28.10.2026 with Tudor: the earlier solver chose the COUNT from the
 *  typed length and kept the length. Typing 2.496 gave 13 risers of 21.5 cm,
 *  red on three rows; typing 4.10 gave treads of 27.3 mm and a run of 4.095,
 *  figures nobody builds. Decided: the count follows the storey, the tread is
 *  a whole centimetre, and a typed length moves to the nearest run the rules
 *  allow — 2.496 and 4.10 both give 4.20, 5.00 gives 4.35.
 *
 *  `treapta` is the play as real bounds, for judging a drawn stair whose
 *  tread was never typed (28.7 is fine); `intregi` are the whole-centimetre
 *  treads inside it, the ones a typed length may take. When no whole
 *  centimetre falls inside (a storey where the band and comfort barely
 *  overlap), the nearest whole centimetre to the band's centre is taken. */
export function lungimileScarii(hNivel: number, rampe = 1): {
  contratrepte: number; pas: number; trepte: number;
  treapta: readonly [number, number]; intregi: readonly number[];
} {
  const k = Math.max(1, Math.floor(rampe));
  const n = Math.max(k + 1, contratrepteComode(hNivel));
  const pas = hNivel / n;
  const jos = Math.max(BLONDEL_MIN - 2 * pas, TREAPTA_CONFORT[0]);
  const sus = Math.min(BLONDEL_MAX - 2 * pas, TREAPTA_CONFORT[1]);
  const intregi: number[] = [];
  for (let cm = Math.ceil(jos * 100 - 1e-6); cm <= Math.floor(sus * 100 + 1e-6); cm++) {
    intregi.push(cm / 100);
  }
  if (!intregi.length) intregi.push(Math.round((BLONDEL_TINTA - 2 * pas) * 100) / 100);
  return { contratrepte: n, pas, trepte: n - k, treapta: [jos, sus], intregi };
}

/** The run nearest a typed length among those the storey allows; `ajustat`
 *  says the typed figure was moved. A tie goes to the shorter run. */
export function lungimeaApropiata(
  lungime: number, hNivel: number, rampe = 1,
): { contratrepte: number; pas: number; treapta: number; lungime: number; ajustat: boolean } {
  const L = lungimileScarii(hNivel, rampe);
  let treapta = L.intregi[0];
  for (const l of L.intregi) {
    if (Math.abs(L.trepte * l - lungime) < Math.abs(L.trepte * treapta - lungime) - 1e-9) treapta = l;
  }
  const run = Math.round(L.trepte * treapta * 1000) / 1000;
  return { contratrepte: L.contratrepte, pas: L.pas, treapta, lungime: run,
           ajustat: Math.abs(run - lungime) > 5e-4 };
}

/** THE STOREY'S TREAD: the whole centimetre nearest the band's centre among
 *  those the storey allows. On 2.80 it is 28, on 3.05 it is 29: 28 there
 *  would leave 2h + l at 61.9, under the band. */
export function treaptaNivelului(hNivel: number): number {
  const bun = lungimileScarii(hNivel);
  const tinta = BLONDEL_TINTA - 2 * bun.pas;
  return bun.intregi.reduce((a, l) =>
    Math.abs(l - tinta) < Math.abs(a - tinta) - 1e-9 ? l : a);
}

/** Lățimea unei rampe de bloc, și implicitul podestului. 1,25: minimul
 *  scării principale la 9 sau mai multe niveluri (R20, NP 057 3.2.A.6.k) —
 *  clădirile platformei au 11; cu 1,10 orice scară nouă s-ar naște roșie. */
export const RAMPA = 1.25;
/** THE LEAST GAP BETWEEN TWO FLIGHTS on the same vertical, in metres: the
 *  team's stair rules ask 20 cm between flights so that fire hoses can be
 *  hauled up the well when the stairwell has no windows for them (rule F39,
 *  §J.4, after P118/1-2025 art. 2.5.6.1.8(2)). The architect drew the same
 *  figure on the sheet (row 20, 21.10.2026): the flights grow inward only
 *  while this much remains between them. */
export const OCHI_MIN = 0.20;

/** Ochiul dintre rampe la scara în U. */
export const OCHI = 0.10;

export type Scara = {
  /** Câte contratrepte urcă nivelul. */
  contratrepte: number;
  /** Înălțimea unei contratrepte, în metri («Actual Riser Height»). */
  pas: number;
  /** Adâncimea unei trepte, în metri («Actual Tread Depth»). */
  treapta: number;
  /** Contratreptele fiecărei rampe, în ordinea urcării. */
  rampe: number[];
  /** Lățimea unei rampe. */
  latRampa: number;
  /** Adâncimea podestului, la scara în U. 0 la cea dreaptă. */
  podest: number;
  /** Lungimea unei rampe pe desen — treptele ei vizibile. */
  lungRampa: number;
};

/** CÂTE CONTRATREPTE URCĂ O ÎNĂLȚIME. Pasul 2 din comentariul de sus. */
export function contratrepte(hNivel: number, pasMax = PAS_MAX): number {
  return Math.max(2, Math.ceil(hNivel / pasMax));
}

/** SCARA DINTR-O CUTIE: câte trepte, cât de adânci, cum se împart pe rampe.
 *
 *  `w` × `h` e cutia trasă de om; `hNivel` e cât are de urcat. La scara în U
 *  cutia ține DOUĂ rampe plus ochiul, și podestul mănâncă din adâncime. */
export function scaraDin(
  fel: "scara" | "scaraU", w: number, h: number, hNivel: number,
): Scara {
  const n = contratrepte(hNivel);
  const pas = hNivel / n;
  if (fel === "scara") {
    // Rampă unică: toate contratreptele pe ea. Treptele DESENATE sunt cu una
    // mai puține — ultima contratreaptă urcă pe planșeul de sus, nu pe o
    // treaptă a rampei.
    const trepte = Math.max(1, n - 1);
    return { contratrepte: n, pas, treapta: h / trepte, rampe: [n],
             latRampa: w, podest: 0, lungRampa: h };
  }
  // Scara în U: contratreptele se împart pe două rampe, prima ia surplusul
  // când numărul e impar — cum le împarte și Revit când tragi a doua rampă.
  const a = Math.ceil(n / 2), b = n - a;
  const latRampa = Math.max(0.6, (w - OCHI) / 2);
  // Podestul ține cât o rampă e de lată — regula de planșă; dar nu poate mânca
  // toată cutia, deci se oprește la jumătate din adâncime.
  const podest = Math.min(latRampa, h * 0.5);
  const lungRampa = Math.max(0.3, h - podest);
  const trepte = Math.max(1, a - 1);
  return { contratrepte: n, pas, treapta: lungRampa / trepte,
           rampe: [a, b], latRampa, podest, lungRampa };
}

// ===========================================================================
// SCARA DESENATĂ DIN RAMPE — cum o face Revit (v. videoul lui Răzvan)
// ===========================================================================
//
// Până aici scara era o CUTIE: o pui, o tragi de colțuri, iar desenul își
// împărțea singur rampele în ea. Dar nimeni nu desenează o scară ca pe un
// dreptunghi — se trage RAMPĂ CU RAMPĂ, iar podestul apare singur între ele:
//
//   1. apeși o dată — acolo începe prima rampă;
//   2. tragi — se așază contratrepte, una la fiecare adâncime de treaptă, și
//      ți se spune câte s-au pus și câte au mai rămas de urcat;
//   3. apeși din nou — rampa s-a terminat;
//   4. începi a doua rampă unde vrei; PODESTUL se face singur, între capătul
//      primei și începutul celei de-a doua;
//   5. când nu mai rămâne nicio contratreaptă, scara e gata.
//
// Deci rampa nu se măsoară în trepte, ci în LUNGIME TRASĂ, iar din lungime ies
// treptele. Linia trasă e linia de urcare — mijlocul rampei —, iar lățimea e a
// tipului de scară, nu a gestului: în video se trage o linie, nu un
// dreptunghi, și cota 113 (apoi 110) stă pe lateral tot timpul.

/** O rampă trasă: linia de urcare, de la primul la ultimul nas de treaptă. */
export type Rampa = { de: [number, number]; la: [number, number] };

/** CÂTE CONTRATREPTE AȘAZĂ O RAMPĂ DE LUNGIMEA ASTA.
 *
 *  O rampă de 196 cm cu treapta de 28 pune 8 contratrepte: 7 trepte întregi
 *  plus cea care urcă pe podest — exact ce scrie Revit în video, «7 RISERS
 *  CREATED» pe o rampă și 16 pe toată scara. De-aia e `+1`, și de-aia o rampă
 *  mai scurtă decât o treaptă pune totuși una: orice linie trasă urcă măcar un
 *  nivel de treaptă. */
export function contratrepteDinLungime(lungime: number, treapta = TREAPTA) {
  if (!(treapta > 0)) return 1;
  // two millimetres of play: a flight off the axes is a whole number of treads
  // with both ends rounded to the millimetre, often a hair under it, and would
  // lose a riser
  return Math.max(1, Math.floor((lungime + 2e-3) / treapta + 1e-9) + 1);
}

/** A DRAWN FLIGHT ENDS ON A WHOLE TREAD, the nearest to where the hand let go,
 *  so every flight draws the tread the panel shows: a free 0.80 m read 0.28 in
 *  the panel and drew two treads of 0.40. */
export function rampaPeTrepte(r: Rampa, treapta = TREAPTA): Rampa {
  const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
  const L = Math.hypot(dx, dy);
  if (!(L > 0)) return r;
  const lung = lungimeDinContratrepte(
    contratrepteDinLungime(L + treapta / 2, treapta), treapta);
  return { de: r.de, la: [r3(r.de[0] + dx / L * lung), r3(r.de[1] + dy / L * lung)] };
}

export type ScaraDesenata = {
  /** Câte contratrepte are scara — ATÂTEA CÂTE S-AU DESENAT, nu câte „trebuie". */
  contratrepte: number;
  /** Înălțimea unei contratrepte: nivelul împărțit la câte sunt. Mai multe
   *  trepte = trepte mai joase. */
  pas: number;
  treapta: number;
  /** Contratreptele fiecărei rampe, în ordinea tragerii. */
  perRampa: number[];
  /** CÂTE AR CERE NIVELUL la pasul maxim admis — o SUGESTIE, nu o limită. */
  sugerate: number;
  /** Câte ar mai trebui ca să se ajungă la sugestie. 0 = s-a ajuns sau s-a
   *  trecut de ea. */
  ramase: number;
};

/** CE A IEȘIT din rampele desenate.
 *
 *  NUMĂRUL DE TREPTE E AL OMULUI, nu al nivelului. Întâi fiecare rampă se oprea
 *  la câte contratrepte mai „rămăseseră" din înălțimea nivelului: o primă rampă
 *  lungă lua toate cele 16, iar a doua rămânea cu 0 — «why are there maximum of
 *  15 steps on the first part and then 0 steps for the second» (05.10.2026).
 *  Înălțimea nivelului SUGEREAZĂ câte trepte se potrivesc (`sugerate`); câte
 *  sunt de fapt spune cine desenează, iar din ele iese înălțimea contratreptei.
 *  O scară cu mai multe trepte are trepte mai joase — și atât. */
export function scaraDinRampe(
  rampe: readonly Rampa[], hNivel: number, treapta = TREAPTA,
): ScaraDesenata {
  const perRampa = rampe.map((r) => contratrepteDinLungime(
    Math.hypot(r.la[0] - r.de[0], r.la[1] - r.de[1]), treapta));
  const total = perRampa.reduce((a, b) => a + b, 0);
  const sugerate = contratrepte(hNivel);
  return { contratrepte: total, pas: total > 0 ? hNivel / total : hNivel,
           treapta, perRampa, sugerate,
           ramase: Math.max(0, sugerate - total) };
}

/** CUTIA ÎN CARE ÎNCAPE SCARA TRASĂ — rampele, lățimea lor și podestele.
 *
 *  Piesa din document rămâne un dreptunghi cu centru, lățime și adâncime (așa o
 *  apucă, o mută și o rotește editorul); ce se schimbă e că la o scară desenată
 *  dreptunghiul e CONSECINȚA rampelor, nu invers. */
export function cutiaRampelor(rampe: readonly Rampa[], latime: number) {
  const xs: number[] = [], ys: number[] = [];
  for (const r of rampe) {
    const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
    const L = Math.hypot(dx, dy) || 1;
    // normala rampei: lățimea se întinde de o parte și de alta a liniei trase
    const nx = (-dy / L) * (latime / 2), ny = (dx / L) * (latime / 2);
    for (const [px, py] of [r.de, r.la]) {
      xs.push(px + nx, px - nx);
      ys.push(py + ny, py - ny);
    }
  }
  if (!xs.length) return null;
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const y0 = Math.min(...ys), y1 = Math.max(...ys);
  return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 };
}

/** PAȘII PRIN CARE SE FACE O SCARĂ, și ce face fiecare apăsare.
 *
 *  Regula gestului, ținută afară din pânză ca să poată fi probată ca SECVENȚĂ
 *  de apăsări, nu ca o funcție chemată o dată (v. «probele pe gest, nu pe
 *  funcție»). `Panza` o cheamă la fiecare apăsare și se mută în starea primită.
 *
 *  LANȚUL E DESCHIS. Întâi erau trei pași și exact două rampe; o scară dreaptă
 *  cu două podeste, sau un U cu trei rampe, nu se puteau face deloc. Acum pașii
 *  se repetă cât vrea omul:
 *
 *    `rampa`  — se trage o rampă (prima) sau i se spun treptele (următoarele);
 *    `latura` — pe ce LATURĂ stă rampa următoare (sus, jos, stânga, dreapta) și
 *               la ce DEPĂRTARE; din ele iese și forma: pe direcția de mers dă
 *               o scară dreaptă cu podest, de-a curmezișul dă un U.
 *
 *  Se încheie când omul spune (`gata: true` pe apăsare — dublu-clic pe pânză),
 *  nu când se termină contratreptele nivelului: câte trepte are scara o spune
 *  tot el (v. `scaraDinRampe`). */
export type FazaScara = "rampa" | "latura";

export type StareScara = {
  faza: FazaScara;
  /** Rampele terminate, în ordinea lor. */
  rampe: Rampa[];
  /** Capătul de pornire al rampei care se trage acum. Doar la prima. */
  de: [number, number] | null;
  la: [number, number];
  /** Latura și depărtarea alese pentru rampa următoare. */
  latura?: Latura;
  dist?: number;
  /** ÎNCOTRO URCĂ rampa care se așază acum. Lipsă = încotro o trimitea regula
   *  veche (v. `sensulImplicit`). Scrisă, e alegerea omului: pe un podest de
   *  colț se poate pleca perpendicular, nu doar înainte sau înapoi. */
  sens?: Latura;
};

export type PasScara =
  | { fel: "stare"; stare: StareScara }
  | { fel: "gata"; rampe: Rampa[] }
  | { fel: "nimic" };

/** Sub atâta o apăsare nu închide o rampă: e a doua apăsare din aceeași,
 *  scăpată de deget, nu o rampă de 3 cm. */
export const RAMPA_MIN = 0.1;

/** Depărtarea cu care pornește o latură nouă: o rampă plus ochiul dintre ele. */
export const DIST_IMPLICITA = RAMPA + 0.1;

export function pasulScarii(
  st: StareScara, q: [number, number], hNivel: number, gata = false,
): PasScara {
  if (gata) {
    return st.rampe.length ? { fel: "gata", rampe: st.rampe } : { fel: "nimic" };
  }
  if (st.faza === "rampa") {
    // PRIMA rampă se trage: o apăsare o deschide, a doua o închide.
    if (!st.rampe.length) {
      if (!st.de) return { fel: "stare", stare: { ...st, de: q, la: q } };
      if (Math.hypot(q[0] - st.de[0], q[1] - st.de[1]) < RAMPA_MIN)
        return { fel: "nimic" };
      return { fel: "stare",
               stare: { ...st, faza: "latura", la: q,
                        rampe: [rampaPeTrepte({ de: st.de, la: q },
                                              treaptaNivelului(hNivel))],
                        latura: "sus", dist: DIST_IMPLICITA } };
    }
    // RAMPELE URMĂTOARE sunt deja așezate (v. `rampaUrmatoare`); apăsarea doar
    // le încheie și trece la latura celei de după.
    // Rampa s-a încheiat: nici sensul, nici latura nu mai sunt ale nimănui.
    //
    // LATURA SE DUCEA MAI DEPARTE, și de-aia fantoma sărea la o scară în U pe
    // care n-o desenase nimeni: «sus» era bun pentru o rampă care urcă, dar
    // dus peste una care merge spre dreapta înseamnă «de-a curmezișul», iar
    // `sensulImplicit` scotea de-acolo o întoarcere de 180° peste rampa tocmai
    // terminată. Se întâmpla la FIECARE încheiere (08.10.2026).
    //
    // Implicitul nou e MERSUL ÎNAINTE: un podest de odihnă pe aceeași linie,
    // cea mai puțin surprinzătoare urmare a unei rampe. Cine vrea să cotească
    // sau să se întoarcă trage cursorul într-acolo.
    const ult = st.rampe.at(-1);
    const inainte = ult
      ? laturaDinVector(ult.la[0] - ult.de[0], ult.la[1] - ult.de[1])
      : "sus";
    return { fel: "stare",
             stare: { ...st, faza: "latura", la: q, sens: undefined,
                      latura: inainte, dist: st.dist ?? DIST_IMPLICITA } };
  }
  // `latura`: s-a ales unde stă următoarea — se adaugă și se trece la treptele ei.
  const ultima = st.rampe.at(-1)!;
  const treapta = treaptaNivelului(hNivel);
  const n = scaraDinRampe([ultima], hNivel, treapta).perRampa[0] ?? 1;
  const latura = st.latura ?? "sus";
  // SENSUL PORNEȘTE DE UNDE ÎL DUCEA REGULA VECHE și se poate schimba pe urmă,
  // trăgând de rampă: cine vrea o scară dreaptă sau în U n-are nimic de făcut,
  // cine vrea un sfert de tur trage în sus.
  const sens = st.sens ?? sensulImplicit(ultima, latura);
  const noua = rampaUrmatoare(ultima, latura, st.dist ?? DIST_IMPLICITA, n,
                              treapta, sens);
  return { fel: "stare",
           stare: { ...st, faza: "rampa", la: q, sens,
                    rampe: [...st.rampe, noua] } };
}

/** LUNGIMEA UNEI RAMPE CU ATÂTEA CONTRATREPTE — inversul lui
 *  `contratrepteDinLungime`, pentru când numărul se SCRIE, nu se trage. */
export function lungimeDinContratrepte(n: number, treapta = TREAPTA) {
  return Math.max(1, n - 1) * treapta;
}

/** A DOUA RAMPĂ, așezată față de prima.
 *
 *  Nu se mai trage liber: se spune la CE DEPĂRTARE stă de prima, iar din
 *  depărtarea aia iese podestul (v. `ScaraDesen::podest`). Rampa a doua pleacă
 *  de pe linia de întoarcere a primei, mutată lateral cu `dist`, și merge
 *  înapoi — e tocmai forma în U.
 *
 *  `dist` e depărtarea dintre liniile de urcare, cu semn: de care parte a
 *  primei rampe stă a doua. */
export function rampaADoua(
  r1: Rampa, dist: number, contratrepteA2: number, treapta = TREAPTA,
): Rampa {
  const dx = r1.la[0] - r1.de[0], dy = r1.la[1] - r1.de[1];
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;
  const nx = -uy, ny = ux;
  const de: [number, number] = [r1.la[0] + nx * dist, r1.la[1] + ny * dist];
  const lung = lungimeDinContratrepte(contratrepteA2, treapta);
  return { de, la: [de[0] - ux * lung, de[1] - uy * lung] };
}

/** Depărtarea (cu semn) de la linia de urcare a rampei până la un punct. */
export function departarea(r: Rampa, p: readonly [number, number]): number {
  const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
  const L = Math.hypot(dx, dy) || 1;
  return ((p[0] - r.la[0]) * -dy + (p[1] - r.la[1]) * dx) / L;
}

/** Unde stă rampa următoare față de cea de acum. În axele planșei: `y` crește
 *  în jos (v. reperul XY din colț), deci «sus» e spre `y` mic. */
export type Latura = "sus" | "jos" | "stanga" | "dreapta";

export const LATURI: Latura[] = ["sus", "jos", "stanga", "dreapta"];

export const VERSOR: Record<Latura, [number, number]> = {
  sus: [0, -1], jos: [0, 1], stanga: [-1, 0], dreapta: [1, 0],
};

/** RAMPA URMĂTOARE, așezată pe o latură, la o depărtare dată.
 *
 *  Scara nu se mai trage rampă cu rampă în gol: după fiecare rampă se spune
 *  CÂT DE DEPARTE stă următoarea și PE CE LATURĂ — sus, jos, stânga, dreapta
 *  (Bianca, 05.10.2026). Din cele două iese și forma scării, fără s-o aleagă
 *  nimeni dintr-o listă:
 *
 *    · latura pe direcția de mers (o rampă care merge spre dreapta, continuată
 *      la «dreapta») = SCARĂ DREAPTĂ cu podest între bucăți: se merge înainte;
 *    · latura de-a curmezișul («sus» sau «jos» la aceeași rampă) = SCARĂ ÎN U:
 *      pe podest te întorci, deci rampa următoare merge invers.
 *
 *  SENSUL SE POATE ȘI ALEGE, de-acum. Scris aici: «sensul NU se alege: iese din
 *  latură», fiindcă cine calcă pe un podest alăturat n-are încotro s-o ia decât
 *  înapoi. Adevărat pentru podestul ALĂTURAT — dar nu toate podestele sunt
 *  alăturate: pe unul de COLȚ se face un sfert de tur, iar rampa următoare
 *  pleacă PERPENDICULAR pe cea dinainte. Cerut, textual: «have run, then set
 *  landing (either up/down or left/right), then be able to have run in each
 *  direction of the landing, then same process» (06.10.2026). Fără `sens`,
 *  regula veche rămâne cuvânt cu cuvânt — și cu ea toate scările drepte și în
 *  U desenate până acum.
 */
export function rampaUrmatoare(
  r: Rampa, latura: Latura, dist: number, contratrepteA: number,
  treapta = TREAPTA, sens?: Latura, latime = RAMPA,
): Rampa {
  const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;
  const [sx, sy] = VERSOR[latura];
  const lung = lungimeDinContratrepte(contratrepteA, treapta);
  if (sens) {
    const [vx, vy] = VERSOR[sens];
    const cotit = Math.abs(ux * vx + uy * vy) < 0.5;
    if (cotit) {
      // PODESTUL DE COLȚ IESE DIN CELE DOUĂ DIRECȚII, nu din latura apăsată.
      //
      // Pe un sfert de tur podestul e PĂTRATUL în care se încrucișează banda
      // rampei dinainte cu banda celei următoare. Deci linia de urcare a
      // rampei noi trece prin mijlocul pătratului pe direcția DE MERS a celei
      // dinainte (o jumătate de lățime dincolo de capătul ei) și începe pe
      // muchia LUI, pe direcția nouă (încă o jumătate de lățime). Amândouă
      // ies din `u` și `v`; `latura` n-are ce căuta aici.
      //
      // SE SOCOTEA DIN `latura`, și mergea doar pe jumătate din cazuri. Când
      // omul ducea cursorul ÎNCOTRO merge rampa următoare — mișcarea firească,
      // chiar cea pe care fantoma o cheamă —, `latura` ieșea egală cu `sens`,
      // amândoi termenii cădeau pe ACEEAȘI axă, și lipsea tragerea înapoi pe
      // direcția de mers: podestul ieșea 1,65 × 1,10 în loc de 1,10 × 1,10,
      // iar ultimele două trepte ale rampei dinainte se desenau PESTE placa
      // lui. Dus dincolo de capăt, același gest ieșea bine — de-aia a trecut
      // proba și de-aia ea a tot spus că nu merge (08.10.2026).
      const de: [number, number] = [
        r3(r.la[0] + ux * (latime / 2) + vx * (latime / 2)),
        r3(r.la[1] + uy * (latime / 2) + vy * (latime / 2))];
      return { de, la: [r3(de[0] + vx * lung), r3(de[1] + vy * lung)] };
    }
    const de: [number, number] = [r3(r.la[0] + sx * dist),
                                  r3(r.la[1] + sy * dist)];
    return { de, la: [r3(de[0] + vx * lung), r3(de[1] + vy * lung)] };
  }
  const de: [number, number] = [r3(r.la[0] + sx * dist), r3(r.la[1] + sy * dist)];
  // de-a lungul mersului sau de-a curmezișul lui?
  const peMers = Math.abs(sx * ux + sy * uy) > Math.abs(sx * -uy + sy * ux);
  const semn = peMers ? 1 : -1;
  return { de, la: [r3(de[0] + ux * lung * semn), r3(de[1] + uy * lung * semn)] };
}

/** SENSUL PE CARE ÎL ALEGEA SINGURĂ regula de mai sus — ca să poată fi pus
 *  într-o stare și arătat omului înainte s-o schimbe. */
export function sensulImplicit(r: Rampa, latura: Latura): Latura {
  const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
  const L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;
  const [sx, sy] = VERSOR[latura];
  const peMers = Math.abs(sx * ux + sy * uy) > Math.abs(sx * -uy + sy * ux);
  const semn = peMers ? 1 : -1;
  return laturaDinVector(ux * semn, uy * semn);
}

/** Cea mai apropiată dintre cele patru laturi, pentru un vector oarecare. */
export function laturaDinVector(vx: number, vy: number): Latura {
  return Math.abs(vx) >= Math.abs(vy)
    ? (vx < 0 ? "stanga" : "dreapta")
    : (vy < 0 ? "sus" : "jos");
}

/** Încotro arată cursorul față de un punct — pentru gestul care alege SENSUL
 *  rampei următoare trăgând de ea. */
export function laturaDe(origine: readonly [number, number],
                         p: readonly [number, number]): Latura {
  return laturaDinVector(p[0] - origine[0], p[1] - origine[1]);
}

/** Latura spre care arată un punct, față de capătul rampei — cea mai apropiată
 *  dintre cele patru. Pentru gestul care alege latura cu cursorul. */
export function laturaSpre(r: Rampa, p: readonly [number, number]): Latura {
  const vx = p[0] - r.la[0], vy = p[1] - r.la[1];
  return Math.abs(vx) >= Math.abs(vy)
    ? (vx < 0 ? "stanga" : "dreapta")
    : (vy < 0 ? "sus" : "jos");
}

/** Cât de departe e punctul, pe latura aleasă. */
export function departareaPe(r: Rampa, latura: Latura,
                             p: readonly [number, number]): number {
  const [sx, sy] = VERSOR[latura];
  return Math.max(0, (p[0] - r.la[0]) * sx + (p[1] - r.la[1]) * sy);
}

const r3 = (v: number) => Math.round(v * 1000) / 1000;

/** SCĂRILE DE-A GATA — formele pe care le vrea aproape toată lumea.
 *
 *  Nu sunt un al doilea fel de scară: fac exact ce ar face mâna desenând, deci
 *  ies RAMPE, iar scara lăsată e editabilă în toate felurile în care e una
 *  trasă — se apucă o rampă, i se spun treptele, se trage de ea. De-aia nici nu
 *  se ține nicăieri „ce preset a fost": după prima mutare n-ar mai fi adevărat.
 *
 *  `trepte` e numărul fiecărei rampe; `dist` depărtarea dintre ele. */
/*  PRESETUL POARTĂ FORMA, NU UN NUMĂR DE TREPTE. Aici scria `trepte: [16]`,
 *  `[8, 8]`, `[6, 5, 5]` — numere adevărate pentru nivelul din videoul lui
 *  Răzvan (2,86 m), și numai pentru el: 16 contratrepte ies dintr-un nivel
 *  între 2,8125 și 3,00. La nivelul IMPLICIT al editorului, 2,80, trebuie 15
 *  (`contratrepte(2,80) = ceil(14,933)`), deci fiecare scară pusă din paletă
 *  ieșea cu o contratreaptă în plus și cu pasul greșit cu 1,17 cm. Numărul se
 *  ia acum din nivelul documentului, la așezare (v. `imparteContratrepte`);
 *  presetul spune doar din CÂTE rampe e făcută forma. */
export type PresetScara = {
  cheie: string; latime: number; rampe: number; laturi: Latura[];
  /** ÎNCOTRO URCĂ fiecare rampă de după prima, când nu e încotro o trimite
   *  latura. Numai scara COTITĂ are nevoie de el. */
  sensuri?: Latura[];
};

export const PRESETURI_SCARA: PresetScara[] = [
  { cheie: "dreapta", latime: RAMPA, rampe: 1, laturi: [] },
  { cheie: "dreapta_podest", latime: RAMPA, rampe: 2, laturi: ["dreapta"] },
  { cheie: "u", latime: RAMPA, rampe: 2, laturi: ["sus"] },
  { cheie: "u_doua", latime: RAMPA, rampe: 3, laturi: ["sus", "sus"] },
  // «L» ERA O COPIE A LUI «U», literă cu literă: aceleași `laturi: ["sus"]`,
  // deci aceleași două rampe invers-paralele. Un L adevărat COTEȘTE — a doua
  // rampă pleacă perpendicular de pe un podest de colț —, iar asta n-a fost cu
  // putință până când rampa n-a căpătat un sens al ei (v. `rampaUrmatoare`).
  { cheie: "l", latime: RAMPA, rampe: 2, laturi: ["dreapta"], sensuri: ["sus"] },
];

// ===========================================================================
// FELUL, ÎMPĂRȚIREA ȘI CUTIA — regulile de care au nevoie și panoul, și macheta
// ===========================================================================

/** «ÎN U» SE HOTĂRĂȘTE DIN ÎNTOARCEREA DRUMULUI, nu din numărul de rampe.
 *
 *  O scară DREAPTĂ cu un podest de odihnă la mijloc are tot două rampe ca una
 *  în U — doar că amândouă merg înainte. Numărate, ies amândouă «scaraU»: pe
 *  planșa Biancăi (06.10.2026) cititorul a dat (3640,1866)→(4040,1866) și
 *  (4440,1866)→(4760,1866), două rampe în prelungire, iar panoul scria «Scară
 *  în U». Promptul importului spune regula de mult («Două rampe pe aceeași
 *  linie, amândouă în același sens, NU sunt o scară în U»); aici n-o aplica
 *  nimeni.
 *
 *  Testul ăsta se făcea deja în două locuri — `ScaraDesen::seIntoarce`, pentru
 *  domul traseului de evacuare, și `arhimport/scara.py` pentru `fel`. Asta e a
 *  treia scriere și ultima: celelalte o cheamă. */
export function felulScarii(rampe: readonly Rampa[]): "scara" | "scaraU" {
  return formaScarii(rampe) === "u" ? "scaraU" : "scara";
}

/** THE TEAM'S STAIR RULES, as the panel checks them (asked by Tudor,
 *  27.10.2026). The source is the team's document "Reguli de proiectare a
 *  casei scării, locuințe colective" (rules R.., F..; the Blondel band is §B);
 *  the tread range is the architect's comfort (21.10.2026), which follows from
 *  the band and R07. Everything here only PAINTS a field: a stair that breaks
 *  a rule is still drawn and saved, the field says which rule and where. */
export const TREPTE_MIN = 3;       // R14 (NP 063 2.2.1.2): no flight under three risers
export const TREPTE_MAX = 16;      // R15 (NP 063 2.2.1.1.2.b): housing, per flight
/** The main stair's clear width at nine levels or more (R20, NP 057 3.2.A.6.k);
 *  the platform's buildings are eleven. */
export const LATIME_PRINCIPALA = 1.25;
/** The usual riser of a main stair (R07, NP 063 rec. 2). */
export const PAS_OBISNUIT: readonly [number, number] = [0.166, 0.175];

export type CampulScarii =
  "pas" | "treapta" | "contratrepte" | "lat" | "podest" | "ochi" | "lungime"
  | "total";

/** One broken rule, addressed to the field that shows the figure. `cheie` is
 *  the message's i18n key and `valori` its figures, already formatted; `k` is
 *  the flight at fault when the rule is one flight's. */
export type AbatereaScarii = {
  camp: CampulScarii;
  cheie: `scara.eroare.${string}`;
  valori: Record<string, string | number>;
  k?: number;
};

/** A tenth of a millimetre: the document writes its figures to the
 *  millimetre, and a riser of 2.80 / 16 must read as exactly 17.5. */
const TOL = 1e-4;

/** WHICH RULES A DRAWN STAIR BREAKS, by field. Pure: the stair as stored
 *  (`treapta_m`, `pas_m`, `podest_m` optional, as in the document) and the
 *  storey it climbs. The figures are resolved the way the panel shows them:
 *  risers from the flights' lengths (`scaraDinRampe`), the riser height from
 *  the written one or the storey's share. */
export function verificaScara(
  sc: { rampe: readonly Rampa[]; latime_m: number; treapta_m?: number;
        pas_m?: number; podest_m?: number },
  hNivel: number,
): AbatereaScarii[] {
  const out: AbatereaScarii[] = [];
  const l = sc.treapta_m ?? TREAPTA;
  const d = scaraDinRampe(sc.rampe, hNivel, l);
  const h = sc.pas_m ?? d.pas;
  const b = blondel(h, l);
  const cm = (v: number, zecimale = 1) => (v * 100).toFixed(zecimale);
  const inBanda = b >= BLONDEL_MIN - TOL && b <= BLONDEL_MAX + TOL;

  // S3 on one flight: a hand-drawn flight can draw another tread than the
  // stair's. Two millimetres of play for coordinates rounded to the millimetre,
  // and the stair's own tread is judged below. First, so that the
  // flight's row names the figure it shows.
  sc.rampe.forEach((r, k) => {
    const trepte = d.perRampa[k] - 1;
    if (trepte < 1) return;
    const lk = Math.hypot(r.la[0] - r.de[0], r.la[1] - r.de[1]) / trepte;
    if (Math.abs(lk - l) < 0.002) return;
    if (lk < TREAPTA_CONFORT[0] - TOL || lk > TREAPTA_CONFORT[1] + TOL) {
      out.push({ camp: "treapta", cheie: "scara.eroare.treapta", valori: { l: cm(lk) }, k });
    }
  });
  // S2 — the riser (R07); S1 — Blondel, on the same field when S2 holds
  if (h < PAS_OBISNUIT[0] - TOL || h > PAS_OBISNUIT[1] + TOL) {
    out.push({ camp: "pas", cheie: "scara.eroare.pas", valori: { h: cm(h) } });
  } else if (!inBanda) {
    out.push({ camp: "pas", cheie: "scara.eroare.blondel", valori: { b: cm(b) } });
  }
  // S3 — the tread; S1 again, on the tread, when S3 holds
  if (l < TREAPTA_CONFORT[0] - TOL || l > TREAPTA_CONFORT[1] + TOL) {
    out.push({ camp: "treapta", cheie: "scara.eroare.treapta", valori: { l: cm(l) } });
  } else if (!inBanda) {
    out.push({ camp: "treapta", cheie: "scara.eroare.blondel", valori: { b: cm(b) } });
  }
  // S1 on the run: the storey's count leaves the tread a few millimetres of
  // play, and the run must be that many treads (`lungimileScarii`). Judged
  // on the tread the run implies, with the tolerance of the riser's and the
  // tread's rows, or a pair a hair inside the band would be quiet on those
  // two rows and red on this one. The message names the runs one can type:
  // the whole-centimetre treads laid end to end.
  const run = sc.rampe.reduce((a, r) =>
    a + Math.hypot(r.la[0] - r.de[0], r.la[1] - r.de[1]), 0);
  const L = lungimileScarii(hNivel, sc.rampe.length);
  const lRun = run / L.trepte;
  if (lRun < L.treapta[0] - TOL || lRun > L.treapta[1] + TOL) {
    out.push({ camp: "lungime", cheie: "scara.eroare.lungime",
               valori: { L: run.toFixed(2), h: hNivel.toFixed(2),
                         min: (L.trepte * L.intregi[0]).toFixed(2),
                         max: (L.trepte * L.intregi[L.intregi.length - 1]).toFixed(2) } });
  }
  // S4, S5 — risers per flight (R14, R15)
  d.perRampa.forEach((n, k) => {
    if (n < TREPTE_MIN || n > TREPTE_MAX) {
      out.push({ camp: "contratrepte", cheie: "scara.eroare.trepte", valori: { n }, k });
    }
  });
  // S6 — the flight's width (R20)
  if (sc.latime_m < LATIME_PRINCIPALA - TOL) {
    out.push({ camp: "lat", cheie: "scara.eroare.lat",
               valori: { w: sc.latime_m.toFixed(2) } });
  }
  // S7 — a landing written narrower than the flight (NP 063 2.2.1.3.c)
  if (sc.rampe.length >= 2 && sc.podest_m != null
      && sc.podest_m < sc.latime_m - TOL) {
    out.push({ camp: "podest", cheie: "scara.eroare.podest",
               valori: { p: sc.podest_m.toFixed(2) } });
  }
  // S7 again, on an in-line landing: between two flights that continue in
  // the same direction the landing is the gap between them, read along the
  // first of the two (after a quarter turn the stair's first flight points
  // elsewhere). `podest_m` never shapes that landing, so its rule above
  // cannot see it; this one answers on the total-length field, the lever
  // that moves the gap, not on the landing panel's depth row.
  for (let k = 1; k < sc.rampe.length; k++) {
    const a = sc.rampe[k - 1], b = sc.rampe[k];
    if (formaScarii([a, b]) !== "dreapta") continue;
    const dx = a.la[0] - a.de[0], dy = a.la[1] - a.de[1];
    const L = Math.hypot(dx, dy) || 1;
    const gol = ((b.de[0] - a.la[0]) * dx + (b.de[1] - a.la[1]) * dy) / L;
    // two millimetres of play: off the axes the ends are rounded
    if (gol < sc.latime_m - 0.002) {
      out.push({ camp: "total", cheie: "scara.eroare.podest",
                 valori: { p: Math.max(0, gol).toFixed(2) }, k: k - 1 });
    }
  }
  // S8 — the eye of a two-flight U (F39): the distance between the ascent
  // lines, measured on the first flight's normal, less one flight's width
  if (sc.rampe.length === 2 && felulScarii(sc.rampe) === "scaraU") {
    const [a, c] = sc.rampe;
    const dx = a.la[0] - a.de[0], dy = a.la[1] - a.de[1];
    const L = Math.hypot(dx, dy) || 1;
    const dist = Math.abs((c.de[0] - a.la[0]) * -dy / L + (c.de[1] - a.la[1]) * dx / L);
    const gol = dist - sc.latime_m;
    if (gol < OCHI_MIN - TOL) {
      out.push({ camp: "ochi", cheie: "scara.eroare.ochi",
                 valori: { g: cm(Math.max(0, gol), 0) } });
    }
  }
  return out;
}

/** Cât de mult trebuie să se abată o rampă de la cea dinainte ca să fie o
 *  ÎNTOARCERE și nu o continuare. `cos 30°`: o rampă trasă de mână „mai
 *  departe pe aceeași linie" cade la un grad-două de cea dinainte, iar una
 *  abătută cu treizeci de grade e o întoarcere pentru orice ochi. */
const COS_PRAG = 0.866;

/** FORMA SCĂRII, din drumul ei — trei, nu două.
 *
 *  `felulScarii` răspunde la întrebarea pe care o pune DOCUMENTUL: felul piesei
 *  e «scara» sau «scaraU», fiindcă atât are catalogul. Dar între cele două
 *  există o a treia formă, pe care planșele o desenează des și pe care n-o
 *  numea nimeni: scara COTITĂ, cu un sfert de tur pe un podest de colț. La
 *  produsul scalar ea dă EXACT 0 — nici întoarcere, nici continuare —, deci
 *  intra tăcut la «dreaptă», iar podestul ei se desena ca o continuare.
 */
export function formaScarii(
  rampe: readonly Rampa[],
): "dreapta" | "cotita" | "u" {
  let cotita = false;
  for (let i = 1; i < rampe.length; i++) {
    const a = rampe[i - 1], b = rampe[i];
    const la = Math.hypot(a.la[0] - a.de[0], a.la[1] - a.de[1]) || 1;
    const lb = Math.hypot(b.la[0] - b.de[0], b.la[1] - b.de[1]) || 1;
    const dot = ((a.la[0] - a.de[0]) * (b.la[0] - b.de[0])
                 + (a.la[1] - a.de[1]) * (b.la[1] - b.de[1])) / (la * lb);
    if (dot < -COS_PRAG) return "u";
    if (dot < COS_PRAG) cotita = true;
  }
  return cotita ? "cotita" : "dreapta";
}

/** CUM SE ÎMPART `n` CONTRATREPTE PE `k` RAMPE: surplusul la primele.
 *
 *  Nu e o regulă nouă — e cea folosită deja de două ori. `scaraDin` dă primei
 *  rampe `ceil(n/2)`, adică 16 → [8, 8]; iar presetul `u_doua` are scris de
 *  mână `[6, 5, 5]`, care e exact 16 pe trei rampe cu surplusul în față. */
export function imparteContratrepte(n: number, k: number): number[] {
  const rampe = Math.max(1, Math.floor(k));
  const total = Math.max(rampe, Math.floor(n));
  const baza = Math.floor(total / rampe);
  const rest = total - baza * rampe;
  return Array.from({ length: rampe }, (_, j) => baza + (j < rest ? 1 : 0));
}

/** `n` RISERS ON THE FLIGHTS AS THEY ARE DRAWN: even flights stay even; on
 *  unequal ones the longest takes the difference, so a typed count keeps the
 *  shape (3 + 13 + 3 at 18 is 3 + 12 + 3, not 6 + 6 + 6). */
export function contratreptePeRampe(per: readonly number[], n: number): number[] {
  if (Math.max(...per) - Math.min(...per) <= 1) return imparteContratrepte(n, per.length);
  const out = [...per];
  const i = out.indexOf(Math.max(...out));
  out[i] += n - out.reduce((a, b) => a + b, 0);
  return out[i] < 1 ? imparteContratrepte(n, per.length) : out;
}

/** RAMPELE DINTR-O CUTIE: cum se face o scară desenată din una pusă ca piesă.
 *
 *  Inversul lui `cutiaRampelor`. Îi trebuie oricui vrea să SPUNĂ ceva despre o
 *  scară adusă din import sau pusă din paletă: alea sunt cutii fără rampe, iar
 *  tot ce știe editorul să spună despre o scară — câte trepte are fiecare
 *  rampă, cât de adânci, cât urcă una — e scris pe rampe. În loc să crească un
 *  al doilea model de scară pentru cutii, cutia se face o dată în rampe și de
 *  acolo încolo e o scară ca oricare alta (v. `model::promoveazaScara`).
 *
 *  NUMĂRUL VINE DIN NIVEL, nu din cutie: înălțimea de urcat e dată, din ea ies
 *  contratreptele, iar lungimea rampei se ia cât îi trebuie lor. Cutia dă
 *  LOCUL și DIRECȚIA — urcarea merge pe lungul ei, ca la simbolul de planșă. */
export function rampeleDinCutie(
  fel: "scara" | "scaraU", c: readonly [number, number], w: number, h: number,
  deg: number, hNivel: number,
): { rampe: Rampa[]; latime: number; treapta: number } {
  const b = scaraDin(fel, w, h, hNivel);
  const a = (deg * Math.PI) / 180, co = Math.cos(a), si = Math.sin(a);
  /** Din cadrul piesei (centru 0, y în jos) în coordonatele planului. */
  const P = (x: number, y: number): [number, number] =>
    [c[0] + x * co - y * si, c[1] + x * si + y * co];
  // RAMPELE UMPLU CUTIA, iar ADÂNCIMEA TREPTEI iese din ea — exact regula pe
  // care o aplică de mult `scaraDin` desenului unei scări puse ca piesă.
  // Promovarea n-are voie să mute scara și nici s-o micșoreze: pe planșă
  // trebuie să rămână exact unde și cum era, altfel omul care scrie un număr
  // de trepte vede scara sărind din casa scării. Adâncimea se și SCRIE în
  // document, fiindcă altfel desenul ar fi numărat treptele cu 0,28 implicit
  // și ar fi ieșit cu alt număr decât cel din panou.
  // ROTUNJITĂ AICI, nu la scriere: lungimile rampelor se socotesc din ea, iar
  // documentul o scrie cu trei zecimale. Socotite din cea nerotunjită, o rampă
  // de 8,35 cu treapta 8,35/15 = 0,556667 se scria 0,557, iar numărătoarea
  // înapoi dădea floor(8,35 / 0,557) + 1 = 15 în loc de 16 — o contratreaptă
  // pierdută din virgula mobilă.
  const treapta = Math.max(0.05, Math.round(b.treapta * 1000) / 1000);
  if (fel === "scara") {
    // O rampă pe mijloc, urcând spre y mic — același sens ca săgeata de urcare
    // din simbolul de planșă (`arhMobilierPlan::Scara`).
    const L = lungimeDinContratrepte(b.rampe[0], treapta);
    return { rampe: [{ de: P(0, L / 2), la: P(0, -L / 2) }],
             latime: Math.max(0.6, w), treapta };
  }
  // ÎN U: două rampe alăturate, podestul la capătul de sus, sensul întors pe a
  // doua — aceeași formă pe care o desenează azi calea cutiei.
  //
  // FIECARE RAMPĂ CU CONTRATREPTELE EI, nu amândouă cât cutia. Le dădeam
  // amândurora toată lungimea, și atunci împărțirea impară a cutiei — 15
  // contratrepte ies [8, 7] — se citea înapoi din rampe ca [8, 8]: numărul din
  // panou sărea de la 15 la 16 chiar în clipa în care omul apăsa prima dată pe
  // scară, fără să fi cerut nimic.
  const x = (b.latRampa + OCHI) / 2;
  const y0 = h / 2, y1 = -h / 2 + b.podest;
  const L1 = Math.max(treapta, lungimeDinContratrepte(b.rampe[0], treapta));
  const L2 = Math.max(treapta, lungimeDinContratrepte(b.rampe[1] ?? b.rampe[0],
                                                      treapta));
  return { rampe: [{ de: P(-x, y0), la: P(-x, y0 - L1) },
                   { de: P(x, y1), la: P(x, y1 + L2) }],
           latime: b.latRampa, treapta };
}
